import {Component, Inject, Optional, ViewChild, ViewChildren, QueryList} from '@angular/core';
import {
  CtBinaryOperator,
  CtDatatableContainerFilterValues,
  CtGenericDialogComponent,
  CtGenericDialogConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelOperator,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  SnackbarService
} from "@ctsolution/ct-framework";
import {ActivatedRoute, Router} from "@angular/router";
import {CompanyService} from "../company.service";
import {CompanyRouteData} from "../../../_core/route-data/company.route-data";
import {CustomerType} from "../../../_core/enum/customer-type";
import {CompanyContactConfiguration, CompanyContactFilter} from "../../company-contact/company-contact.configuration";
import {ContactCompany} from "../../company-contact/contact-company";
import {ContactCompanyType} from "../../../_core/enum/contact-company-type";
import {ContactCompanyRouteData} from "../../../_core/route-data/contact-company.route-data";
import {ContactRouteData} from "../../../_core/route-data/contact.route-data";
import {
  CompanyContactRelationService
} from "../../company-contact/company-contact-relation/company-contact-relation.service";
import {CtManagerService} from "../../../_core/lib/ct-manager.service";
import {CompanyEditFormComponent} from './company-edit-form/company-edit-form.component';
import {FormGroup} from '@angular/forms';
import {forkJoin, Observable, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {CtWebapiGenericResponse} from "@ctsolution/ct-webapi";
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog
} from "@angular/material/legacy-dialog";
import { SchedulerListConfiguration } from '../../scheduler/scheduler-list/scheduler-list.configuration';
import { SchedulerService } from '../../scheduler/scheduler.service';
import { CTConfigurationModel } from '../../../_core/classes/ct-configuration-model';
import { 
  CompanyDuplicateConfirmationDialogComponent, 
  CompanyDuplicateConfirmationResult 
} from './company-duplicate-confirmation-dialog/company-duplicate-confirmation-dialog.component';

@Component({
  selector: 'app-company-edit',
  templateUrl: './company-edit.component.html',
})
export class CompanyEditComponent {

  @ViewChild(CompanyEditFormComponent) companyEditFormComponent!: CompanyEditFormComponent;
  @ViewChild('companyInformationComponent') companyInformationComponent: any;
  @ViewChild('companyPlaceListComponent') companyPlaceListComponent: any;

  companyModelRouteData: CtModelRouteData = CompanyRouteData().setModelType(CtModelType.FORM);
  companyModelConfiguration: CtModelConfiguration<any> = CtModelConfiguration.create();
  companyContactModelConfiguration: CompanyContactConfiguration | null = null;
  datatableContainerFilterValues: CtDatatableContainerFilterValues | null = null;
  leadOid: string | null = null;
  schedulerListConfiguration: SchedulerListConfiguration | null = null;
  leadCompanyConfiguration : CTConfigurationModel = CTConfigurationModel.create();
  contactCompanyConfiguration : CTConfigurationModel = CTConfigurationModel.create();
  // Visibilita' della sezione Contatti, dichiarabile per-client nel MenuJson. La decide il padre,
  // come per Lead e Riferimenti: se la sezione non viene creata, il salvataggio la salta da solo.
  companyInformationConfiguration : CTConfigurationModel = CTConfigurationModel.create();

  calendarVisible: boolean = false;
  isSupplier: boolean = false;

  // Stato « Abilitato » di partenza: in modifica quello caricato nel form dal record, in creazione
  // il default del form. Serve a chiedere conferma solo quando l'utente lo cambia davvero.
  private initialActive: boolean = true;

  get enableMasterDetail(): boolean {

    return !!this.companyModelConfiguration?.RouteData?.id;

  }

  get dialogData() : boolean{

    return !!this.dialogConfiguration;

  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private modelService: CtModelService<any>,
    private companyService: CompanyService,
    private schedulerService : SchedulerService,
    private companyContactRelationService: CompanyContactRelationService,
    private managerService : CtManagerService,
    private dialog: MatLegacyDialog,
    private snackbarService: SnackbarService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogConfiguration: any | undefined,
    private dialogRef: MatDialogRef<CompanyEditComponent>) {

    this.companyModelConfiguration
      ?.setRouteData(this.companyModelRouteData);

    const tpValue: string | null = this.route.snapshot.paramMap.get("type");

    if (!tpValue) return;

    this.isSupplier = this.companyService.getCustomerTypeByRouteValue(tpValue) === CustomerType.Supplier;

    this.setupConfiguration().then()

  }

  async setupConfiguration() {

    const pId: string | null = this.route.snapshot.paramMap.get("id");

    this.leadOid = this.route.snapshot.queryParamMap.get("LeadOid");

    this.leadCompanyConfiguration.setVisible(await this.managerService.hasEnabled.CRM() ?? false);

    this.calendarVisible = await this.managerService.hasEnabled.calendar()?? false;

    this.managerService.getComponentOverride("company", "leadCompanyConfiguration", this.leadCompanyConfiguration).then((component) => {
      return;
    });

    this.managerService.getComponentOverride("company", "contactCompanyConfiguration", this.contactCompanyConfiguration).then((component) => {
      return;
    });

    this.managerService.getComponentOverride("company", "companyInformationConfiguration", this.companyInformationConfiguration).then((component) => {
      return;
    });

    if (pId) {

      this.companyModelRouteData.setId(+pId);

      this.modelService
        .getInfos(this.companyModelConfiguration)
        .subscribe({
          next: async (response) => {

            const dataSource = response?.Result?.DataSource;

            if (dataSource) {

              this.companyEditFormComponent.setup(dataSource);

              // Letto dal form e non dal record: e' lo stato che l'utente vede, anche se il record
              // non portasse il campo e il control restasse al suo default.
              this.initialActive = !!this.companyEditFormComponent.configuration?.form?.get('Active')?.value;

            }

          },
          error: (err) => console.error('Errore durante il recupero delle informazioni:', err)
        })

      await this.setupCompanyContactModelConfiguration();

      const param = this.schedulerService.returnParam(+pId);

      if(await this.managerService.hasEnabled.calendar()){

        this.schedulerListConfiguration = new SchedulerListConfiguration(
        [ CTModelDatatableFilter.create()
          .setField("[Lead.Customer.Oid] == ?")
          .setValue([+pId])
          .setOperatorType(CtBinaryOperator.OrOperator),
          CTModelDatatableFilter.create()
          .setField("[WorkOrder.Company.Oid] == ?")
          .setValue([+pId])
          .setOperatorType(CtBinaryOperator.OrOperator),
        ],
          param
        ).setOnlyUpdate(true)
        .setAction(() => this.submit(this.companyEditFormComponent!.configuration.form));

        const tpValue: string | null = this.route.snapshot.paramMap.get("type");

        if (tpValue) {
          this.schedulerListConfiguration.setExtraUrlParam(tpValue);
        }
    }


    }

    this.route.data.subscribe(data => data["title"] = this.isSupplier ? "CT_MENU.supplier" : "CT_MENU.customers");
    this.route.queryParams.subscribe(params => {
      const q = params['q'];
      if (q) {

        const filterValues = JSON.parse(q);
        this.datatableContainerFilterValues = CtDatatableContainerFilterValues.create(filterValues);

      }
    });


  }

  /**
   * Controlla se esiste già una company con lo stesso codice fiscale o partita IVA
   * @param fiscalCode Codice fiscale da controllare
   * @param vat Partita IVA da controllare
   * @param currentOid OID della company corrente (se in modalità edit, per escluderla dalla ricerca)
   * @returns Promise<boolean> True se esiste già un duplicato, false altrimenti
   */
  private async checkDuplicateCompany(fiscalCode: string | null, vat: string | null, currentOid: number | null): Promise<boolean> {
    
    // Se entrambi i campi sono vuoti, non c'è nulla da controllare
    if (!fiscalCode && !vat) {
      return false;
    }

    // Creo la configurazione per la chiamata al model service usando il controller di Company
    const companyRouteData: CtModelRouteData = CompanyRouteData();
    const configuration: CtModelConfiguration<any> = CtModelConfiguration
      .create()
      .setRouteData(companyRouteData);

    // Creo l'array di filtri per la ricerca
    const filters: CTModelDatatableFilter[] = [];

    // Se sono in modalità edit, escludo la company corrente dalla ricerca
    // Filtro: { "Value": 111643, "OperatorType": 1, "Field": "Oid" }
    // TODO: Verifica che il NotEqual sia l'operatore corretto per escludere l'Oid corrente
    if (currentOid) {
      filters.push(
        CTModelDatatableFilter.create()
          .setField('Oid')
          .setValue(currentOid)
          .setOperatorType(CtBinaryOperator.NotEqual) // OperatorType: 1
      );
    }

    // Aggiungo il filtro per la partita IVA solo se valorizzata
    // Filtro: { "Value": ["00544980121"], "OperatorType": 53, "Field": "[VAT] is not null AND Contains([VAT], ?)" }
    if (vat && vat.trim() !== '') {
      filters.push(
        CTModelDatatableFilter.create()
          .setField('[VAT] is not null AND Contains([VAT], ?)')
          .setValue([vat.trim()])
          .setOperatorType(CtBinaryOperator.OrOperator) // 53
      );
    }

    // Aggiungo il filtro per il codice fiscale solo se valorizzato
    // Filtro: { "Value": ["00544980121"], "OperatorType": 53, "Field": "[FiscalCode] is not null AND Contains([FiscalCode], ?)" }
    if (fiscalCode && fiscalCode.trim() !== '') {
      filters.push(
        CTModelDatatableFilter.create()
          .setField('[FiscalCode] is not null AND Contains([FiscalCode], ?)')
          .setValue([fiscalCode.trim()])
          .setOperatorType(CtBinaryOperator.OrOperator) // 53
      );
    }

    // Aggiungo il filtro per IsCustomer = true
    // Filtro: { "Value": true, "OperatorType": 0, "Field": "IsCustomer" }
    filters.push(
      CTModelDatatableFilter.create()
        .setField('IsCustomer')
        .setValue(true)
        .setOperatorType(CtBinaryOperator.Equal) // 0
    );

    // Se non ci sono filtri da applicare (oltre a IsCustomer), non c'è nulla da controllare
    if (filters.length <= 1) {
      return false;
    }

    // Creo il body per la chiamata con i filtri
    const body: CtModelDatatableOperators = CtModelDatatableOperators
      .create()
      .setFilters(filters);

    try {
      // Eseguo la chiamata al backend per ottenere la lista delle company
      const response: CtWebapiGenericResponse<any> = await this.modelService
        .getList(configuration, body)
        .toPromise();

      // Se il TotalElement è maggiore di 0, vuol dire che esiste già una company con uno dei due campi
      return (response?.Result?.TotalElement ?? 0) > 0;

    } catch (error) {
      console.error('Errore durante il controllo del duplicato company:', error);
      // In caso di errore, per sicurezza procedo senza bloccare l'utente
      return false;
    }
  }

  /**
   * Mostra la modale di conferma per il duplicato
   * @returns Promise<boolean> True se l'utente conferma, false altrimenti
   */
  private async showDuplicateConfirmationDialog(): Promise<boolean> {
    
    const dialogRef = this.dialog.open(CompanyDuplicateConfirmationDialogComponent, {
      width: '500px',
      disableClose: true // L'utente deve scegliere un'azione
    });

    const result: CompanyDuplicateConfirmationResult = await dialogRef.afterClosed().toPromise();

    return result?.confirmed ?? false;
  }

  /**
   * Chiede conferma quando lo stato « Abilitato » del cliente cambia rispetto a quello di partenza:
   * abilitare o disabilitare un cliente ha effetto su tutto il gestionale, e va fatto
   * consapevolmente. I fornitori sono esclusi, la richiesta riguarda i soli clienti.
   * @returns true se si puo' procedere con il salvataggio
   */
  private async confirmActiveChange(active: boolean): Promise<boolean> {

    if (this.isSupplier || active === this.initialActive) return true;

    const messageKey = active ? 'company.activeConfirmation.enable' : 'company.activeConfirmation.disable';

    const dialogRef = this.dialog.open(CtGenericDialogComponent, {
      data: {
        configuration: CtGenericDialogConfiguration.create()
          .setTitle('company.activeConfirmation.title')
          .setContent(`${messageKey}.message`)
          .setSubContent(`${messageKey}.detail`)
          .setCancelLabel('CT_PAGE.CT_FORM.cancel')
          .setSubmitLabel('CT_PAGE.CT_FORM.confirm')
      },
      maxWidth: '500px',
      minWidth: '300px',
      disableClose: true // come per i duplicati: l'utente deve scegliere un'azione
    });

    return !!(await dialogRef.afterClosed().toPromise());
  }

  /**
   * Procede con il salvataggio effettivo della company
   * @param value I dati del form da salvare
   */
  private proceedWithSubmit(value: any): void {
    
    this.modelService
      .putInfos(this.companyModelConfiguration, value)
      ?.subscribe({
        next: (response: CtWebapiGenericResponse<number | null>) => {

          if(this.leadOid){

            this.companyService.navigateToLead(this.leadOid);
            return;

          }

          const commands = ['/', 'customer'];

          if (response.Result && !isNaN(response?.Result)) {

            // Riferimento automatico dichiarato nel MenuJson: va creato prima di lasciare la
            // pagina, altrimenti il cliente nasce senza.
            this.ensureDefaultContactReference(response.Result);

            if(this.dialogData){

              this.dialogRef.close(response);
              return

            }

            commands
              .push(
                this.isSupplier ? 'supplier' : 'private',
                'edit',
                response.Result?.toString());

            this.router
              .navigate(commands, { queryParamsHandling: 'preserve' });

          } else {

            this.setupConfiguration();

          }

        },
        error: (err) => console.error('Errore durante l\'operazione:', err)
      });
  }

  /**
   * Submit sincronizzato di tutte le sezioni (company-edit-form, company-information, company-place-list)
   * Questo metodo viene chiamato da qualsiasi pulsante "Salva" nelle sezioni figlie
   * 
   * Flusso:
   * 1. Valida tutti i form (marca come touched per mostrare errori)
   * 2. Se tutti validi, esegue i submit in parallelo con forkJoin
   * 3. Mostra notifica di successo/errore
   * 4. Refresh della pagina
   */
  async submitAll() {

    // 1. Ottieni il pId per verificare se siamo in modalità edit
    const pId: string | null = this.route.snapshot.paramMap.get("id");
    
    // Questo metodo funziona solo in modalità edit (quando abbiamo l'Oid della company)
    if (!pId) {
      console.error('submitAll() può essere utilizzato solo in modalità edit');
      return;
    }

    // 2. Valida tutti i form e marca come touched
    const allFormsValid = this.validateAllForms();

    // 3. Se almeno un form non è valido, stoppo e mostro gli errori
    if (!allFormsValid) {
      this.snackbarService.open('Alcuni campi non sono validi. Controlla i form.');
      return;
    }

    // 4. Preparo il form principale per il controllo duplicati
    const formGroup = this.companyEditFormComponent?.configuration?.form;
    if (!formGroup) {
      console.error('Form principale non disponibile');
      return;
    }

    const value = formGroup.getRawValue();
    value[this.isSupplier ? 'IsSupplier' : 'IsCustomer'] = true;

    const Tags: { Oid: number }[] = (value?.Tags ?? []).map((tag: { value: any; }) => ({ Oid: tag.value }));
    value['Tags'] = Tags;

    // 5. Controllo duplicati per FiscalCode e VAT
    const fiscalCode: string | null = value?.FiscalCode ?? null;
    const vat: string | null = value?.VAT ?? null;
    const currentOid: number = +pId;

    const isDuplicate = await this.checkDuplicateCompany(fiscalCode, vat, currentOid);

    if (isDuplicate) {
      const userConfirmed = await this.showDuplicateConfirmationDialog();
      if (!userConfirmed) {
        return;
      }
    }

    // Conferma del cambio di stato « Abilitato »: annullando, non si salva nessuna sezione
    if (!(await this.confirmActiveChange(!!value?.Active))) return;

    // 6. Eseguo i submit di tutte le sezioni in parallelo
    const submitObservables = {
      companyForm: this.submitCompanyForm(value),
      companyInformation: this.submitCompanyInformation(),
      companyPlaces: this.submitCompanyPlaces()
    };

    // 7. ForkJoin attende che tutti i submit siano completati
    forkJoin(submitObservables)
      .pipe(
        catchError(error => {
          console.error('Errore durante il submit sincronizzato:', error);
          this.snackbarService.open('Errore durante il salvataggio. Riprova.');
          return of(null);
        })
      )
      .subscribe(results => {
        
        if (results) {
          // 8. Tutti i submit sono andati a buon fine
          this.snackbarService.open('Tutte le modifiche sono state salvate con successo');

          // Riferimento automatico dichiarato nel MenuJson: garantito anche sui clienti gia'
          // esistenti, che potrebbero non averlo.
          this.ensureDefaultContactReference(this.companyModelRouteData?.id ?? null);
          
          // 9. Refresh della pagina
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      });
  }

  /**
   * Valida tutti i form e li marca come touched per mostrare gli errori
   * Utilizza un approccio "safe" per evitare errori su form non completamente inizializzati
   * @returns true se tutti i form sono validi, false altrimenti
   */
  private validateAllForms(): boolean {
    let allValid = true;

    // Valida company-edit-form (sempre presente)
    try {
      const companyForm = this.companyEditFormComponent?.configuration?.form;
      if (companyForm) {
        this.safeMarkAsTouched(companyForm);
        if (!companyForm.valid) {
          allValid = false;
          console.warn('Company form non valido');
        }
      }
    } catch (error) {
      console.error('Errore durante la validazione del company form:', error);
      allValid = false;
    }

    // Valida company-information (solo se presente e ha un form)
    try {
      if (this.companyInformationComponent?.modelConfiguration?.form) {
        const infoForm = this.companyInformationComponent.modelConfiguration.form;
        this.safeMarkAsTouched(infoForm);
        if (!infoForm.valid) {
          allValid = false;
          console.warn('Company information form non valido');
        }
      }
    } catch (error) {
      console.error('Errore durante la validazione del company information form:', error);
      // Se il form non è inizializzato correttamente, lo skippo ma non blocco il salvataggio
      console.warn('Company information form skippato per errore di inizializzazione');
    }

    // Valida company-place-list (tutti i form dei places)
    try {
      if (this.companyPlaceListComponent?.configurations) {
        this.companyPlaceListComponent.configurations.forEach((config: CtModelConfiguration<any>, index: number) => {
          try {
            if (config.form) {
              this.safeMarkAsTouched(config.form);
              if (!config.form.valid) {
                allValid = false;
                console.warn(`Company place ${index + 1} non valido`);
              }
            }
          } catch (placeError) {
            console.error(`Errore durante la validazione del place ${index + 1}:`, placeError);
            // Skippo questo place specifico ma continuo con gli altri
          }
        });
      }
    } catch (error) {
      console.error('Errore durante la validazione dei company places:', error);
      console.warn('Company places skippati per errore di inizializzazione');
    }

    return allValid;
  }

  /**
   * Marca un form come touched in modo sicuro, evitando errori su controlli non inizializzati
   * Questo è necessario perché alcuni form vengono caricati dinamicamente da template JSON
   * e potrebbero avere controlli nel template che non sono ancora nel FormGroup
   * @param form Il FormGroup da marcare come touched
   */
  private safeMarkAsTouched(form: FormGroup): void {
    try {
      // Invece di markAllAsTouched, itero manualmente sui controlli esistenti
      Object.keys(form.controls).forEach(key => {
        try {
          const control = form.get(key);
          if (control) {
            control.markAsTouched();
            // Se il controllo è un FormGroup o FormArray, marca ricorsivamente
            if (control instanceof FormGroup) {
              this.safeMarkAsTouched(control);
            }
          }
        } catch (controlError) {
          console.warn(`Impossibile marcare il controllo '${key}' come touched:`, controlError);
          // Continua con gli altri controlli
        }
      });
    } catch (error) {
      console.error('Errore durante safeMarkAsTouched:', error);
      // Fallback: prova comunque markAllAsTouched
      try {
        form.markAllAsTouched();
      } catch (fallbackError) {
        console.error('Anche il fallback markAllAsTouched è fallito:', fallbackError);
      }
    }
  }

  /**
   * Submit del form principale company-edit-form
   * @param value I dati del form già processati
   * @returns Observable<any>
   */
  private submitCompanyForm(value: any): Observable<any> {
    return new Observable(observer => {
      this.modelService
        .putInfos(this.companyModelConfiguration, value)
        ?.subscribe({
          next: (response: CtWebapiGenericResponse<number | null>) => {
            observer.next(response);
            observer.complete();
          },
          error: (err) => {
            console.error('Errore durante il salvataggio company form:', err);
            observer.error(err);
          }
        });
    });
  }

  /**
   * Submit del form company-information
   * @returns Observable<any>
   */
  private submitCompanyInformation(): Observable<any> {
    // Se non c'è il componente o non ha un form, ritorno un Observable vuoto (successo)
    if (!this.companyInformationComponent?.submitForm) {
      return of({ success: true, message: 'No company information to save' });
    }

    // Chiamo il metodo submitForm esposto dal componente figlio
    return this.companyInformationComponent.submitForm();
  }

  /**
   * Submit di tutti i company places
   * @returns Observable<any>
   */
  private submitCompanyPlaces(): Observable<any> {
    // Se non ci sono places da salvare, ritorno un Observable vuoto (successo)
    if (!this.companyPlaceListComponent?.submitAllPlaces) {
      return of({ success: true, message: 'No company places to save' });
    }

    // Chiamo il metodo submitAllPlaces esposto dal componente figlio
    return this.companyPlaceListComponent.submitAllPlaces();
  }

  /**
   * Metodo legacy per submit singolo del form principale
   * Manteniamo questo metodo per retrocompatibilità, ma in modalità edit
   * dovrebbe essere usato submitAll() per sincronizzare tutte le sezioni
   */
  async submit(formGroup: FormGroup | null) {

    if (!formGroup) return;

    formGroup.markAllAsTouched();

    if (formGroup.valid) {

      const value = formGroup.getRawValue();
      value[this.isSupplier ? 'IsSupplier' : 'IsCustomer'] = true;

      const Tags: { Oid: number }[] = (value?.Tags ?? []).map((tag: { value: any; }) => ({ Oid: tag.value }));
      value['Tags'] = Tags;

      // Estraggo i valori di FiscalCode e VAT per il controllo duplicati
      const fiscalCode: string | null = value?.FiscalCode ?? null;
      const vat: string | null = value?.VAT ?? null;

      // Recupero l'OID corrente se sono in modalità edit (pId dal route)
      const pId: string | null = this.route.snapshot.paramMap.get("id");
      const currentOid: number | null = pId ? +pId : null;

      // Controllo se esiste già una company con gli stessi dati
      const isDuplicate = await this.checkDuplicateCompany(fiscalCode, vat, currentOid);

      if (isDuplicate) {
        // Se esiste un duplicato, mostro la modale di conferma
        const userConfirmed = await this.showDuplicateConfirmationDialog();

        if (!userConfirmed) {
          // L'utente ha annullato, non procedo con il salvataggio
          return;
        }
      }

      // Conferma del cambio di stato « Abilitato » (es. cliente creato gia' disabilitato)
      if (!(await this.confirmActiveChange(!!value?.Active))) return;

      // Se non c'è duplicato o l'utente ha confermato, procedo con il salvataggio
      this.proceedWithSubmit(value);

    }

  }

  async setupCompanyContactModelConfiguration() {

    // il seguente if abilita la sezione riferimenti solo in determinati casi. mi è stato chiesto di rimuoverlo, ma per sicurezza lascio il codice
    // const CRM = await this.managerService.hasEnabled.CRM() ?? false;
    //
    // if (!CRM) return;

    this.companyContactModelConfiguration = CompanyContactConfiguration
      .create()
      // Operazioni dal MenuJson: un ruolo che non deve toccare i riferimenti dichiara un elenco
      // vuoto. Se il JSON non dichiara nulla restano le tre storiche.
      .setOperations(this.contactCompanyConfiguration.datatableOperations
        ?? [CtModelOperator.Create, CtModelOperator.Update, CtModelOperator.Delete])
      .setCompanyContactFilter(
        CompanyContactFilter
          .create()
          .setField('Companies.Oid')
          .setValue(this.companyModelRouteData.id))
      .setOnCreate(() => this.addContact())
      .setOnEdit(async contactRelation => {

        const returnData = await this.companyContactRelationService.companyContactRelationEdit(contactRelation)

        if (returnData) window.location.reload();

      })
      .setFieldsCustomTemplates([
        {prop: 'Companies', visible: false},
        {prop: 'CompaniesType', visible: false}
      ]);

  }

  /**
   * Crea il riferimento Cliente↔Contatto dichiarato nel MenuJson, se non c'e' gia'.
   * Senza dichiarazione non fa nulla: gli altri client non sono toccati.
   *
   * Il contatto si dichiara col **codice** e non con l'Oid: l'Oid cambia da un ambiente
   * all'altro, il codice no. Viene risolto qui, prima di creare la relazione.
   */
  private ensureDefaultContactReference(companyOid: number | null): void {

    const reference = this.contactCompanyConfiguration.defaultContactReference;

    if (!reference?.contactCode || !companyOid) return;

    this.getContactOidByCode(reference.contactCode)
      .then(contactOid => {

        if (!contactOid) {

          console.error(`Riferimento predefinito non creato: nessun contatto con codice ${reference.contactCode}.`);
          return;

        }

        this.createContactReferenceIfMissing(companyOid, contactOid, reference.type);

      });

  }

  /**
   * Risolve l'Oid di un contatto dal suo codice. Restituisce null se non esiste.
   */
  private getContactOidByCode(code: string): Promise<number | null> {

    const operators = CtModelDatatableOperators
      .create()
      .setFilters([
        CTModelDatatableFilter.create().setField('Code').setValue(code).setOperatorType(CtBinaryOperator.Equal)
      ]);

    return new Promise<number | null>(resolve => {

      this.modelService
        .getList(CtModelConfiguration.create().setRouteData(ContactRouteData()), operators)
        .subscribe({
          next: (response: CtWebapiGenericResponse<any>) => {

            const contacts = (response?.Result?.DataSource ?? []) as any[];

            if (contacts.length > 1) {
              console.warn(`Piu' contatti con codice ${code}: viene usato il primo.`);
            }

            resolve(contacts[0]?.Oid ?? null);

          },
          error: (err: any) => {

            console.error('Errore durante la ricerca del contatto predefinito:', err);
            resolve(null);

          }
        });

    });

  }

  /**
   * Crea la relazione Cliente↔Contatto se non esiste gia': il controllo evita il doppione
   * quando il cliente viene risalvato.
   */
  private createContactReferenceIfMissing(companyOid: number, contactOid: number, type: number): void {

    const operators = CtModelDatatableOperators
      .create()
      .setFilters([
        CTModelDatatableFilter.create().setField('Companies.Oid').setValue(companyOid).setOperatorType(CtBinaryOperator.Equal),
        CTModelDatatableFilter.create().setField('Contacts.Oid').setValue(contactOid).setOperatorType(CtBinaryOperator.Equal)
      ]);

    this.modelService
      .getList(CtModelConfiguration.create().setRouteData(ContactCompanyRouteData()), operators)
      .subscribe({
        next: (response: CtWebapiGenericResponse<any>) => {

          const existing = (response?.Result?.DataSource ?? []) as any[];

          if (existing.length) return;

          const value: ContactCompany = ContactCompany
            .create()
            .setContactsOid(contactOid)
            .setCompaniesOid(companyOid)
            .setType(type);

          this.companyContactRelationService
            .companyContactRelationInsert(value)
            ?.subscribe({
              next: () => { },
              error: (err: any) => console.error('Errore durante la creazione del riferimento predefinito:', err)
            });

        },
        error: (err: any) => console.error('Errore durante la verifica del riferimento predefinito:', err)
      });

  }

  private async addContact() {

    const oid = await this.companyContactRelationService.openBinder();

    if (oid) {

      const modelValue: ContactCompany = ContactCompany
        .create()
        .setContactsOid(oid)
        .setCompaniesOid(this.companyModelRouteData?.id ?? -1)
        .setType(ContactCompanyType.Defined);

      this.companyContactRelationService
        .companyContactRelationInsert(modelValue)
        ?.subscribe(() => window.location.reload());

      // dovrei usare la navigazione sul router, però non sta funzionando correttamente ... per ora facciamo refresh forzato

    }

  }

}

export enum CustomCompanySetupEnum {

  BASE = 1,
  CUSTOM = 2,
  BOTH = 3

}
